import { Box, Button, FormControl, FormLabel, Heading, Textarea } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ConfirmDialog } from '../../../components/ConfirmDialog'
import PublicFormLayout from '../../../components/publicFormLayout/PublicFormLayout'
import { useAppToast } from '../../../helpers'
import ExperienceFeedbackService from '../../../services/ExperienceFeedback/ExperienceFeedbackService'
import {
  IExperienceFeedback,
  IExperienceFeedbackAnswer,
  IExperienceFeedbackSummary,
  IQuestion
} from '../../../services/ExperienceFeedback/interfaces'

// The completion screen is public and cannot call the authenticated answers endpoint,
// so the result travels in the navigation state.
const summaryOf = (feedback: IExperienceFeedback): IExperienceFeedbackSummary | null =>
  feedback.score != null && feedback.max_score != null && feedback.result != null
    ? { score: feedback.score, max_score: feedback.max_score, result: feedback.result }
    : null

export default function ExperienceFeedbackStepPage() {
  const navigate = useNavigate()
  const toast = useAppToast()
  const [saving, setSaving] = useState(false)
  const experienceFeedbackService = useMemo(() => new ExperienceFeedbackService(), [])
  const [formAnswers, setFormAnswers] = useState<IExperienceFeedbackAnswer>({})
  const { id } = useParams()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [experienceFeedback, setExperienceFeedback] = useState<IExperienceFeedback>({
    id: 0,
    name: '',
    email: '',
    sector_id: 0,
    manager: '',
    step: 0,
    created_at: '',
    link_pdf: '',
    score: null,
    max_score: null,
    result: null,
    grades: null
  })
  const [question, setQuestion] = useState<IQuestion>({
    id: 0,
    order: 0,
    title: '',
    description: '',
    type: '',
    options: null
  })

  const fetchExperienceFeedback = async () => {
    try {
      const response = await experienceFeedbackService.getExperienceFeedback(Number(id))
      setExperienceFeedback(response.data)
    } catch {
      toast.error('Erro ao encontrar a Avaliação!')
    }
  }

  const fetchQuestion = async () => {
    if (experienceFeedback.step > 0) {
      try {
        const response = await experienceFeedbackService.getQuestion(Number(id))
        setQuestion(response.data)
        return response.data
      } catch {
        toast.error('Erro ao buscar as perguntas da etapa da Avaliação de Experiência!')
      }
    } else {
      navigate(`/experience-feedback/complete`, { state: { summary: summaryOf(experienceFeedback) } })
    }
  }

  useEffect(() => {
    fetchExperienceFeedback()
  }, [])

  useEffect(() => {
    if (experienceFeedback.id > 0) {
      fetchQuestion()
    }
  }, [experienceFeedback])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSaving(true)

    // valida se todos os campos foram preenchidos
    let hasEmptyField = false
    if (!formAnswers[question.id]) {
      hasEmptyField = true
      setSaving(false)
      return
    }

    if (hasEmptyField) {
      toast.warning('Preencha todos os campos')
      setSaving(false)
      return
    }

    try {
      const response = await experienceFeedbackService.update(Number(id), formAnswers)
      if (response.data.success) {
        if (response.data.next_question > 0) {
          fetchQuestion()
        } else {
          navigate(`/experience-feedback/complete`, { state: { summary: response.data.summary } })
        }
      } else {
        toast.error('Erro ao salvar respostas da Avaliação de Experiência!')
      }
      setFormAnswers({})
    } catch {
      toast.error('Erro ao salvar respostas da Avaliação de Experiência!')
    }

    setSaving(false)
  }

  const handlePrevious = async () => {
    try {
      setFormAnswers({})
      const response = await experienceFeedbackService.previousStep(Number(id))
      const newQuestion = await fetchQuestion()
      if (newQuestion) {
        setFormAnswers({ [newQuestion.id]: response.data.actual_step_answer.value })
      }
    } catch {
      toast.error('Erro ao retornar a etapa anterior!')
    } finally {
      setIsModalOpen(false)
    }
  }

  return (
    <>
      <PublicFormLayout title="Avaliação de Experiência">
        <Heading as="h2" mb="4">
          {question.title}
        </Heading>

        <form onSubmit={handleSubmit}>
          <FormControl isRequired={true} key={question.id} mb="4">
            {question.description && <FormLabel whiteSpace="pre-line">{question.description}</FormLabel>}
            {question.type === 'options' && question.options && (
              <Box display="flex" flexDirection="column" gap={2}>
                {question.options.map((option, index) => (
                  <Button
                    key={index}
                    onClick={() => setFormAnswers({ ...formAnswers, [question.id]: option })}
                    colorScheme={formAnswers[question.id] === option ? 'magenta' : 'gray'}
                    variant={formAnswers[question.id] === option ? 'solid' : 'outline'}
                  >
                    {option}
                  </Button>
                ))}
              </Box>
            )}
            {question.type === 'textarea' && (
              <Box display="flex" flexDirection="column" gap={2}>
                <Textarea
                  value={formAnswers[question.id] || ''}
                  onChange={(event) => setFormAnswers({ ...formAnswers, [question.id]: event.target.value })}
                  height="360px"
                />
              </Box>
            )}
          </FormControl>

          <Box display={'flex'} justifyContent={'space-between'}>
            {question.order > 1 && (
              <Button colorScheme="magenta" disabled={saving} onClick={() => setIsModalOpen(true)}>
                Voltar
              </Button>
            )}

            <Button colorScheme="magenta" disabled={saving} type="submit">
              Próximo
            </Button>
          </Box>
        </form>
      </PublicFormLayout>

      <ConfirmDialog
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handlePrevious}
        action="save"
        title="Voltar para etapa anterior?"
        description="Ao voltar para a etapa anterior, você perderá as respostas já salvas."
        confirmLabel="Voltar"
      />
    </>
  )
}
